import type { PusherPrivateChannel } from '@shared/api'

import { leaveChannel, listenChannel, stopListeningEvent, useChannel } from '@shared/api'
import { useToast } from '@shared/ui/toast'

import { DisciplineScheduleStep } from './enums'
import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { useDisciplineScheduleResults } from './discipline-schedule-results.hook'

const ALLOCATION_DONE_EVENT = '.discipline.schedule.allocation.done'
const ALLOCATION_FAILED_EVENT = '.discipline.schedule.allocation.failed'

const getRunId = (data: unknown): number | null =>
  Number((data as { activation_run_id?: number } | null)?.activation_run_id) || null

/**
 * The tutor search reports only over the discipline channel, so the page joins it before any search is started.
 * A finished run is remembered even when its id is not known yet: the event can beat the allocate response.
 */
export const useDisciplineScheduleEvents = (): void => {
  const route = useRoute()
  const toast = useToast()
  const store = useDisciplineScheduleStore()
  const { canViewDisciplines } = useDisciplinePrivileges()
  const { loadResults } = useDisciplineScheduleResults()

  const channelName = `admin.discipline.${Number(route.params.id)}`

  let channel: PusherPrivateChannel | null = null

  const isWaitingFor = (runId: number): boolean =>
    runId === store.getActivationRunId && store.getStep === DisciplineScheduleStep.AssignTutor

  const onDone = (data: unknown): void => {
    const runId = getRunId(data)
    if (!runId) return

    store.addDoneRunId(runId)
    if (isWaitingFor(runId)) void loadResults(runId)
  }

  const onFailed = (data: unknown): void => {
    const runId = getRunId(data)
    if (!runId || !isWaitingFor(runId)) return

    toast.error({ title: 'Error!', description: 'Tutor availability request failed!' })
    store.resetAllocation()
    store.setStep(DisciplineScheduleStep.Filters)
  }

  onMounted(() => {
    if (!canViewDisciplines.value) return

    channel = useChannel(channelName)
    listenChannel(channel, ALLOCATION_DONE_EVENT, onDone)
    listenChannel(channel, ALLOCATION_FAILED_EVENT, onFailed)
  })

  onBeforeUnmount(() => {
    if (!channel) return

    stopListeningEvent(channel, ALLOCATION_DONE_EVENT, onDone)
    stopListeningEvent(channel, ALLOCATION_FAILED_EVENT, onFailed)
    leaveChannel(channelName)
    channel = null
  })
}
